// @ts-nocheck — TODO: add full TypeScript types in a follow-up PR
import { buildAuthHeaders, fetchWithAuth, getApiOrigin, localeHeader, tryShellRefresh } from '@blpde/connect';

const DEFAULT_DRIVE_ENDPOINT = '/v1';
const DEFAULT_DRIVE_MODULE = 'drive';

export const driveConfig = {
  get baseUrl() {
    return getApiOrigin();
  },
  endpoint: import.meta.env.VITE_DRIVE_API_ENDPOINT || DEFAULT_DRIVE_ENDPOINT,
  module: import.meta.env.VITE_DRIVE_API_MODULE || DEFAULT_DRIVE_MODULE,
};

/** The format catalog. The browser revalidates it against its ETag. */
export async function fetchDriveFormats() {
  const response = await driveRequest({ path: '/formats' });
  return Array.isArray(response?.formats) ? response.formats : [];
}

export async function fetchDriveAccount() {
  const response = await driveRequest({ path: '/accounts/me' });
  return response || null;
}

export async function fetchDriveMounts() {
  const response = await driveRequest({ path: '/mounts' });
  return normalizeDriveMounts(response);
}

export async function fetchDriveMountCredentialStatus({ mountId } = {}) {
  const response = await driveRequest({
    path: `/mounts/${encodeURIComponent(mountId)}/credentials/status`,
  });

  return normalizeDriveMountCredentialStatus(response);
}

export async function saveDriveMountCredentials({ domain = '', mountId, password, username } = {}) {
  return driveRequest({
    body: {
      domain,
      password,
      username,
    },
    method: 'POST',
    path: `/mounts/${encodeURIComponent(mountId)}/credentials`,
  });
}

export async function deleteDriveMountCredentials({ mountId } = {}) {
  return driveRequest({
    method: 'DELETE',
    path: `/mounts/${encodeURIComponent(mountId)}/credentials`,
  });
}

/** The caller's own mount ("Mein Drive") in the session's company; created on first use. */
export async function ensurePersonalDriveMount() {
  const mount = await driveRequest({ method: 'POST', path: '/mounts/personal' });
  return { id: normalizeDriveId(mount?.id), name: String(mount?.name || '') };
}

export async function fetchDriveFolder({ mountId, path = '', preview = false } = {}) {
  const response = await driveRequest({
    path: `/mounts/${encodeURIComponent(mountId)}/list`,
    query: preview ? { path, preview: 'true' } : { path },
  });

  return normalizeDriveEntries(response);
}

export async function searchDriveIndex({ limit, mountId, query, subtree = '' } = {}) {
  const params = { q: query };
  if (subtree) params.subtree = subtree;
  if (limit) params.limit = limit;
  const response = await driveRequest({
    path: `/mounts/${encodeURIComponent(mountId)}/index/search`,
    query: params,
  });
  return normalizeDriveIndexResponse(response);
}

export async function fetchDriveIndexStats({ mountId } = {}) {
  const response = await driveRequest({
    path: `/mounts/${encodeURIComponent(mountId)}/index/stats`,
  });
  return {
    mountId: normalizeDriveId(response?.mountId),
    count: Number(response?.count) || 0,
  };
}

export async function fetchDriveUsage({ mountId } = {}) {
  try {
    const response = await driveRequest({
      path: `/mounts/${encodeURIComponent(mountId)}/usage`,
    });

    return normalizeDriveUsage(response);
  } catch (error) {
    if (Number(error?.status) === 501) {
      error.code = 'not_supported';
    }
    throw error;
  }
}

export async function fetchDriveEntry({ mountId, path, preview = false } = {}) {
  return driveRequest({
    path: `/mounts/${encodeURIComponent(mountId)}/stat`,
    query: preview ? { path, preview: 'true' } : { path },
  });
}

export async function resolveDriveFolder({ mountId, documentId, permission = 'read' }) {
  return driveRequest({
    path: `/mounts/${encodeURIComponent(mountId)}/folders/${encodeURIComponent(documentId)}`,
    query: { permission },
  });
}

export async function createDriveFolder({ mountId, path }) {
  return driveRequest({
    body: { path },
    method: 'POST',
    path: `/mounts/${encodeURIComponent(mountId)}/mkdir`,
  });
}

export async function moveDriveEntry({ from, mountId, to, ifMatch = '' }) {
  const etag = ifMatch || (await fetchDriveEntry({ mountId, path: from }))?.etag;
  if (!etag) throw new DriveApiError({ error: 'revision_unavailable' }, 409);
  return driveRequest({
    body: { from, to, overwrite: false },
    headers: { 'If-Match': etag },
    method: 'POST',
    path: `/mounts/${encodeURIComponent(mountId)}/move`,
  });
}

export async function copyDriveEntry({ from, mountId, to, ifMatch = '' }) {
  const etag = ifMatch || (await fetchDriveEntry({ mountId, path: from }))?.etag;
  if (!etag) throw new DriveApiError({ error: 'revision_unavailable' }, 409);
  return driveRequest({
    body: { from, to },
    headers: { 'If-Match': etag },
    method: 'POST',
    path: `/mounts/${encodeURIComponent(mountId)}/copy`,
  });
}

export async function deleteDriveEntry({ mountId, path, ifMatch = '' }) {
  const etag = ifMatch || (await fetchDriveEntry({ mountId, path }))?.etag;
  if (!etag) throw new DriveApiError({ error: 'revision_unavailable' }, 409);
  return driveRequest({
    body: { path },
    method: 'POST',
    // Older servers hard-delete through DELETE /file. An explicit trash
    // endpoint must refuse safely until the reversible operation is available.
    path: `/mounts/${encodeURIComponent(mountId)}/trash`,
    headers: { 'If-Match': etag },
  });
}

export type DriveTrashEntry = {
  id: string;
  mountId: number;
  mountName: string;
  documentId?: string;
  name: string;
  isDir: boolean;
  mimeType: string;
  size: number;
  originalPath: string;
  deletedAt: string;
  deletedById: number;
  deletedByName?: string;
  etag: string;
  status?: 'active' | 'pending' | 'restoring' | 'purging' | 'conflict';
};

/** All trash records visible to the caller, across their accessible mounts. */
export async function fetchDriveTrash(): Promise<DriveTrashEntry[]> {
  const response = await driveRequest({ path: '/trash' });
  return normalizeDriveTrashEntries(response);
}

/** Restore only the trashed revision; never stat a replacement at its old path. */
export async function restoreDriveTrashEntry({
  mountId,
  trashId,
  ifMatch,
}: {
  mountId: number | string;
  trashId: string;
  ifMatch: string;
}): Promise<{ entry: Record<string, unknown>; path: string }> {
  if (!ifMatch) throw new DriveApiError({ error: 'revision_unavailable' }, 409);
  return driveRequest({
    method: 'POST',
    path: `/mounts/${encodeURIComponent(mountId)}/trash/${encodeURIComponent(trashId)}/restore`,
    headers: { 'If-Match': ifMatch },
  });
}

export async function deleteDriveTrashEntry({
  mountId,
  trashId,
  ifMatch,
}: {
  mountId: number | string;
  trashId: string;
  ifMatch: string;
}): Promise<{ id: string }> {
  if (!ifMatch) throw new DriveApiError({ error: 'revision_unavailable' }, 409);
  return driveRequest({
    method: 'DELETE',
    path: `/mounts/${encodeURIComponent(mountId)}/trash/${encodeURIComponent(trashId)}`,
    headers: { 'If-Match': ifMatch },
  });
}

export function normalizeDriveTrashEntries(body: unknown): DriveTrashEntry[] {
  return getCollection(body, ['entries']).flatMap((entry) => {
    const id = String(entry?.id || '');
    const mountId = normalizeDriveId(entry?.mountId);
    if (!id || mountId === null) return [];
    const originalPath = String(entry?.originalPath || '');
    return [
      {
        ...entry,
        id,
        mountId,
        mountName: String(entry?.mountName || ''),
        documentId: String(entry?.documentId || ''),
        name: String(entry?.name || originalPath.split('/').pop() || ''),
        isDir: Boolean(entry?.isDir),
        mimeType: String(entry?.mimeType || ''),
        size: Number(entry?.size) || 0,
        originalPath,
        deletedAt: String(entry?.deletedAt || ''),
        deletedById: normalizeDriveId(entry?.deletedById) || 0,
        deletedByName: String(entry?.deletedByName || ''),
        etag: String(entry?.etag || ''),
        status: String(entry?.status || 'active'),
      },
    ];
  });
}

// Files above this size go through a resumable upload session in server-sized
// parts (every request body stays under the platform's 250 MiB cap); smaller
// files keep the single multipart upload.
export const UPLOAD_SESSION_THRESHOLD = 64 * 1024 * 1024;
const UPLOAD_PART_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

export async function uploadDriveFile(options = {}) {
  const { file, ifMatch, createOnly = false } = options;
  if (ifMatch && createOnly) throw new Error('Conflicting upload preconditions');
  if (Number(file?.size) > UPLOAD_SESSION_THRESHOLD) return uploadDriveFileInSession(options);
  return uploadDriveFileSingle(options);
}

async function uploadDriveFileSingle({ file, mountId, onProgress, path = '', signal, ifMatch, createOnly = false } = {}) {
  if (ifMatch && createOnly) throw new Error('Conflicting upload preconditions');
  // createDriveHeaders hands out a plain object; the preconditions need `set`.
  const uploadHeaders = new Headers(createDriveHeaders({ isMultipart: true }));
  if (ifMatch) uploadHeaders.set('If-Match', ifMatch);
  if (createOnly) uploadHeaders.set('If-None-Match', '*');
  const formData = new globalThis.FormData();
  formData.append('file', file);

  const url = resolveDriveUrl(`/mounts/${encodeURIComponent(mountId)}/upload`, { path });

  // Prefer XHR when available so we can surface upload progress events;
  // fall back to fetch in environments (jsdom, tests) where XHR is missing.
  if (typeof globalThis.XMLHttpRequest !== 'function') {
    const response = await fetchWithAuth(url, {
      body: formData,
      headers: uploadHeaders,
      method: 'POST',
      signal,
    });
    const body = await readDriveBody(response);
    if (!response.ok) {
      throw createDriveError(body, response.status);
    }
    notifyDriveDownloadProgress(onProgress, file?.size || 0, file?.size || 0);
    return { ...body, etag: response.headers.get('ETag') || body?.etag || '' };
  }

  // XHR can't go through `fetchWithAuth`, so fold the shell bearer in here.
  const headers = buildAuthHeaders(uploadHeaders);

  return new Promise((resolve, reject) => {
    const xhr = new globalThis.XMLHttpRequest();
    xhr.open('POST', url, true);

    headers.forEach((headerValue, headerName) => {
      try {
        xhr.setRequestHeader(headerName, headerValue);
      } catch {
        // Some headers (Content-Length, etc.) cannot be set programmatically.
      }
    });

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        notifyDriveDownloadProgress(onProgress, event.loaded, event.total);
      } else if (file?.size) {
        notifyDriveDownloadProgress(onProgress, event.loaded, file.size);
      }
    });

    xhr.addEventListener('load', () => {
      const responseText = xhr.responseText || '';
      let parsed = null;
      if (responseText) {
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = responseText;
        }
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        notifyDriveDownloadProgress(onProgress, file?.size || 0, file?.size || 0);
        resolve({
          ...parsed,
          etag: xhr.getResponseHeader?.('ETag') || parsed?.etag || '',
          documentId: String(parsed?.documentId || ''),
          revision: Number(parsed?.revision) || 0,
        });
      } else {
        reject(createDriveError(parsed, xhr.status));
      }
    });

    xhr.addEventListener('error', () => {
      reject(createDriveError({ error: 'network_error' }, 0));
    });

    xhr.addEventListener('abort', () => {
      reject(createDriveError({ error: 'upload_aborted' }, 0));
    });

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(formData);
  });
}

function uploadAbortedError() {
  return createDriveError({ error: 'upload_aborted' }, 0);
}

function isUploadRetryable(error) {
  return (
    error instanceof DriveApiError &&
    (error.status === 0 || error.status >= 500) &&
    error.status !== 507 &&
    error.code !== 'upload_aborted'
  );
}

// A 401 of the bearer (not the mount's own credentials, which are a 401 with
// their own code): the token ran out during a long upload.
function isUploadTokenExpired(error) {
  return error instanceof DriveApiError && error.status === 401 && !isDriveCredentialsError(error);
}

function waitForUploadRetry(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(uploadAbortedError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(uploadAbortedError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// The session state of a create / part / status answer. An answer that does not
// carry a usable state (a body cut off after the 200, an HTML error page of a
// proxy) is a transport failure, not progress: it surfaces as a retryable
// status-0 error and never replaces the state the client already holds.
function parseUploadState(body) {
  const partSize = Number(body?.partSize);
  const partCount = Number(body?.partCount);
  const nextPart = Number(body?.nextPart);
  if (
    typeof body?.id !== 'string' ||
    !body.id ||
    !(partSize > 0) ||
    !Number.isInteger(partCount) ||
    partCount < 1 ||
    !Number.isInteger(nextPart) ||
    nextPart < 1 ||
    nextPart > partCount + 1
  ) {
    throw createDriveError({ error: 'invalid_response' }, 0);
  }
  return { id: body.id, size: Number(body.size) || 0, partSize, partCount, nextPart };
}

// The file Entry a completed session answers with; anything else is an unsure
// outcome and gets the (idempotent) complete again.
function parseUploadEntry(body) {
  if (!body || typeof body !== 'object' || (typeof body.path !== 'string' && typeof body.name !== 'string')) {
    throw createDriveError({ error: 'invalid_response' }, 0);
  }
  return body;
}

// Where the session's file goes: the same rule as the single upload (the
// server's ResolveUploadPath) — an empty path or one ending in a slash names
// the folder and takes the file's own name.
function resolveUploadTargetPath(path, fileName) {
  const destination = String(path || '');
  if (destination !== '' && !/[\\/]$/.test(destination)) return destination;
  const name = String(fileName || '').replace(/\\/g, '/').split('/').pop();
  if (!name || name === '.' || name === '..') throw createDriveError({ error: 'invalid_path' }, 400);
  return `${destination}${name}`;
}

// JSON request of the session protocol. Transport failures surface as
// DriveApiError('network_error') / ('upload_aborted'), HTTP failures as the
// server's error body.
async function uploadSessionRequest({ body, headers = {}, method, path, query, signal }) {
  let response;
  try {
    response = await fetchWithAuth(resolveDriveUrl(path, query), {
      body: body ? JSON.stringify(body) : undefined,
      headers: { ...createDriveHeaders(), ...headers },
      method,
      signal,
    });
  } catch {
    throw signal?.aborted ? uploadAbortedError() : createDriveError({ error: 'network_error' }, 0);
  }
  return { body: await readUploadBody(response, signal), etag: response.headers.get('ETag') || '' };
}

// The body of a session answer. A read that breaks off after a 2xx is a
// transport failure (the outcome is unsure and the caller retries through the
// status or complete); an error answer keeps its status even when its body
// cannot be read.
async function readUploadBody(response, signal) {
  let body = null;
  try {
    body = await readDriveBody(response);
  } catch {
    if (response.ok) throw signal?.aborted ? uploadAbortedError() : createDriveError({ error: 'network_error' }, 0);
  }
  if (!response.ok) throw createDriveError(body, response.status);
  return body;
}

// One part as a raw body, through XHR when available so the progress covers the
// bytes actually on the wire; fetch is the fallback for jsdom and tests.
async function sendUploadPart({ blob, onLoaded, signal, url }) {
  const partHeaders = new Headers({
    Accept: 'application/json',
    'BLP-Module': driveConfig.module,
    'Content-Type': 'application/octet-stream',
  });

  if (typeof globalThis.XMLHttpRequest !== 'function') {
    let response;
    try {
      response = await fetchWithAuth(url, {
        body: blob,
        headers: partHeaders,
        method: 'PUT',
        signal,
      });
    } catch {
      throw signal?.aborted ? uploadAbortedError() : createDriveError({ error: 'network_error' }, 0);
    }
    return readUploadBody(response, signal);
  }

  const headers = buildAuthHeaders(partHeaders);
  return new Promise((resolve, reject) => {
    const xhr = new globalThis.XMLHttpRequest();
    xhr.open('PUT', url, true);
    headers.forEach((headerValue, headerName) => {
      try {
        xhr.setRequestHeader(headerName, headerValue);
      } catch {
        // Some headers (Content-Length, etc.) cannot be set programmatically.
      }
    });
    xhr.upload.addEventListener('progress', (event) => onLoaded(event.loaded));
    xhr.addEventListener('load', () => {
      let parsed = null;
      if (xhr.responseText) {
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = { message: xhr.responseText };
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
      else reject(createDriveError(parsed, xhr.status));
    });
    xhr.addEventListener('error', () => reject(createDriveError({ error: 'network_error' }, 0)));
    xhr.addEventListener('timeout', () => reject(createDriveError({ error: 'network_error' }, 0)));
    xhr.addEventListener('abort', () => reject(uploadAbortedError()));
    if (signal) {
      if (signal.aborted) {
        reject(uploadAbortedError());
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(blob);
  });
}

// Upload through `POST /mounts/{id}/uploads`: create, send the parts strictly in
// order, complete. A part that fails on the wire is resumed from the server's
// `nextPart`, so accepted parts are never sent twice. A token that expires
// during a long upload is renewed through the shell and the session goes on.
// The completion is idempotent on the server, so an unsure outcome of it is
// answered with the same request again, never with a status read.
async function uploadDriveFileInSession({
  file,
  mountId,
  onProgress,
  path = '',
  signal,
  ifMatch,
  createOnly = false,
} = {}) {
  const base = `/mounts/${encodeURIComponent(mountId)}/uploads`;
  const total = Number(file.size) || 0;
  const preconditions = {};
  if (ifMatch) preconditions['If-Match'] = ifMatch;
  if (createOnly) preconditions['If-None-Match'] = '*';
  const targetPath = resolveUploadTargetPath(path, file?.name);

  if (signal?.aborted) throw uploadAbortedError();
  const created = await uploadSessionRequest({
    body: { size: total, mediaType: file.type || undefined },
    headers: preconditions,
    method: 'POST',
    path: base,
    query: { path: targetPath },
    signal,
  });
  let session = parseUploadState(created.body);
  const sessionPath = `${base}/${encodeURIComponent(session.id)}`;
  const doneBytes = (nextPart) => Math.min(total, Math.max(0, nextPart - 1) * session.partSize);

  // Sends every part from the session's nextPart on. Wire failures, 5xx and
  // unreadable answers are retried with backoff after a status read; a 401
  // renews the token first and does not count as a failure.
  const sendRemainingParts = async () => {
    let failures = 0;
    let authRenewals = 0;
    let resync = false;
    for (;;) {
      try {
        if (signal?.aborted) throw uploadAbortedError();
        if (resync) {
          // After a transport failure the server's state is the only truth.
          const state = await uploadSessionRequest({ method: 'GET', path: sessionPath, signal });
          session = { ...session, ...parseUploadState(state.body) };
          resync = false;
          notifyDriveDownloadProgress(onProgress, doneBytes(session.nextPart), total);
        }

        while (session.nextPart <= session.partCount) {
          const part = session.nextPart;
          const start = (part - 1) * session.partSize;
          const end = Math.min(total, start + session.partSize);
          try {
            const body = await sendUploadPart({
              blob: file.slice(start, end),
              onLoaded: (loaded) =>
                notifyDriveDownloadProgress(onProgress, start + Math.min(loaded, end - start), total),
              signal,
              url: resolveDriveUrl(`${sessionPath}/parts/${part}`),
            });
            session = { ...session, ...parseUploadState(body) };
            failures = 0;
            authRenewals = 0;
            notifyDriveDownloadProgress(onProgress, doneBytes(session.nextPart), total);
          } catch (error) {
            const resumeAt = Number(error?.body?.nextPart);
            if (error?.status === 409 && error.code === 'part_out_of_order' && resumeAt >= 1) {
              if (resumeAt === part) throw error;
              session = { ...session, nextPart: resumeAt };
              notifyDriveDownloadProgress(onProgress, doneBytes(resumeAt), total);
              continue;
            }
            throw error;
          }
        }
        return;
      } catch (error) {
        if (isUploadTokenExpired(error) && authRenewals < 2 && (await tryShellRefresh())) {
          authRenewals += 1;
          resync = true;
          continue;
        }
        if (!isUploadRetryable(error) || failures >= UPLOAD_PART_RETRY_DELAYS_MS.length) throw error;
        const delay = UPLOAD_PART_RETRY_DELAYS_MS[failures];
        failures += 1;
        resync = true;
        await waitForUploadRetry(delay, signal);
      }
    }
  };

  // POST …/complete, repeated with backoff while the outcome is unsure (wire
  // failure, unreadable answer, 5xx): the server answers a session that already
  // completed with the same Entry again.
  const complete = async () => {
    let authRenewals = 0;
    for (let attempt = 0; ; attempt += 1) {
      try {
        const done = await uploadSessionRequest({ method: 'POST', path: `${sessionPath}/complete`, signal });
        return { entry: parseUploadEntry(done.body), etag: done.etag };
      } catch (error) {
        if (error?.status === 409 && error.code === 'upload_already_completed') {
          throw createDriveError(
            {
              ...error.body,
              error: 'upload_already_completed',
              message:
                error.body?.message ||
                'The upload finished, but the file was overwritten by someone else in the meantime.',
            },
            409,
          );
        }
        // The token can run out right at the end of a long upload as well.
        if (isUploadTokenExpired(error) && authRenewals < 2 && (await tryShellRefresh())) {
          authRenewals += 1;
          attempt -= 1;
          continue;
        }
        if (!isUploadRetryable(error) || attempt >= UPLOAD_PART_RETRY_DELAYS_MS.length) throw error;
        await waitForUploadRetry(UPLOAD_PART_RETRY_DELAYS_MS[attempt], signal);
      }
    }
  };

  try {
    let incomplete = 0;
    notifyDriveDownloadProgress(onProgress, doneBytes(session.nextPart), total);

    for (;;) {
      await sendRemainingParts();
      try {
        const done = await complete();
        notifyDriveDownloadProgress(onProgress, total, total);
        return {
          ...done.entry,
          etag: done.etag || done.entry.etag || '',
          documentId: String(done.entry.documentId || ''),
          revision: Number(done.entry.revision) || 0,
        };
      } catch (error) {
        const resumeAt = Number(error?.body?.nextPart);
        if (error?.status === 409 && error.code === 'upload_incomplete' && resumeAt >= 1 && ++incomplete <= 3) {
          session = { ...session, nextPart: resumeAt };
          continue;
        }
        throw error;
      }
    }
  } catch (error) {
    // Aborted or failed for good: free the server-side parts. Fire and forget,
    // without the (possibly aborted) signal.
    uploadSessionRequest({ method: 'DELETE', path: sessionPath }).catch(() => {});
    throw signal?.aborted && error?.code !== 'upload_aborted' ? uploadAbortedError() : error;
  }
}

export async function downloadDriveFile({ mountId, onProgress, path, signal } = {}) {
  const response = await fetchWithAuth(resolveDriveUrl(`/mounts/${encodeURIComponent(mountId)}/download`, { path }), {
    headers: createDriveHeaders({ accept: '*/*', isMultipart: true }),
    method: 'GET',
    // Never from or into the HTTP cache: a cached copy makes the browser
    // revalidate with If-None-Match, which the download door once refused.
    cache: 'no-store',
    signal,
  }, { signal });

  if (!response.ok) {
    const body = await readDriveBody(response);
    throw createDriveError(body, response.status);
  }

  const result = await collectDriveDownload(response, onProgress, path);
  // A download is bytes, not JSON, so the identity travels in headers
  // alongside the ETag. Both are explicitly CORS-exposed by the backend;
  // without that the browser would hide them from this code.
  return {
    ...result,
    etag: response.headers.get('ETag') || '',
    documentId: response.headers.get('BLP-Document-Id') || '',
    revision: Number(response.headers.get('BLP-Revision')) || 0,
  };
}

// Anonymous share-token URLs always include `module=drive` so the master
// public proxy dispatches them to the drive module — anonymous requests
// don't go through createDriveHeaders, so the BLP-Module header is absent
// and the query parameter is the only routing signal the proxy gets.
function buildShareTokenUrl(
  baseUrl: string,
  token: string,
  { suffix = '', path = '' }: { suffix?: string; path?: string } = {},
): string {
  const apiBase = resolveDriveApiBase(baseUrl).replace(/\/+$/, '');
  const params = [`module=${encodeURIComponent(driveConfig.module)}`];
  if (path) {
    params.push(`path=${encodeURIComponent(path)}`);
  }
  return `${apiBase}/shares/by-token/${encodeURIComponent(token)}${suffix}?${params.join('&')}`;
}

/**
 * Anonymous: resolve a link share token. Returns shape:
 *   { mountId, path, permission, expiresAt, name }
 * `name` is derived from the final path segment for display.
 */
export async function fetchDriveShareByToken({ baseUrl = '', token = '' }: { baseUrl?: string; token?: string } = {}) {
  const url = buildShareTokenUrl(baseUrl, token);
  // Anonymous: no bearer, but the UI language, so an error reads in it.
  const response = await fetch(url, { headers: { Accept: 'application/json', ...localeHeader() } });
  if (response.status === 404) {
    throw createDriveError({ message: 'share_not_found' }, 404);
  }
  if (response.status === 410) {
    throw createDriveError({ message: 'share_expired' }, 410);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw createDriveError(body, response.status);
  }
  const body = await response.json();
  const sharePath = String(body.path || '');
  const segments = sharePath.split('/').filter(Boolean);
  return {
    mountId: normalizeDriveId(body.mountId),
    // The backend resolves the share through its document id and answers
    // with where the object lives now, so a share keeps working after
    // somebody renamed the file behind it.
    path: sharePath,
    documentId: String(body.documentId || ''),
    permission: String(body.permission || 'read'),
    expiresAt: normalizeNullableString(body.expiresAt) || null,
    name: segments[segments.length - 1] || sharePath || 'Share',
  };
}

/**
 * Anonymous file download via a share token. Returns the same `{ blob, fileName }`
 * shape as `downloadDriveFile`, with the same on-progress contract.
 */
export async function downloadDriveShareByToken({
  baseUrl = '',
  onProgress,
  path = '',
  token = '',
}: { baseUrl?: string; onProgress?: any; path?: string; token?: string } = {}) {
  const url = buildShareTokenUrl(baseUrl, token, { suffix: '/download', path });
  const response = await fetch(url, {
    cache: 'no-store',
    headers: { Accept: 'application/octet-stream', ...localeHeader() },
  });
  if (response.status === 404) throw createDriveError({ message: 'share_not_found' }, 404);
  if (response.status === 410) throw createDriveError({ message: 'share_expired' }, 410);
  if (response.status === 403) throw createDriveError({ message: 'share_forbidden' }, 403);
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw createDriveError(body, response.status);
  }
  return collectDriveDownload(response, onProgress, path);
}

/**
 * Anonymous URL — used by `<a href>` direct-download links.
 */
export function resolveDriveShareDownloadUrl({
  baseUrl = '',
  path = '',
  token = '',
}: { baseUrl?: string; path?: string; token?: string } = {}) {
  return buildShareTokenUrl(baseUrl, token, { suffix: '/download', path });
}

export async function fetchDriveShares() {
  const response = await driveRequest({ path: '/shares' });
  return normalizeDriveShares(response);
}

export async function createDriveShare({ expiresAt = '', mountId, path, permission = 'read', targetAccountId, type = 'link' }) {
  const body = {
    mountId: Number(mountId),
    path,
    permission,
    targetAccountId,
    type,
  };
  if (expiresAt) {
    body.expiresAt = expiresAt;
  }

  const response = await driveRequest({
    body,
    method: 'POST',
    path: '/shares',
  });
  return normalizeDriveShare(response) || response;
}

export async function revokeDriveShare({ shareId }) {
  await driveRequest({
    method: 'DELETE',
    path: `/shares/${encodeURIComponent(shareId)}`,
  });
}

/**
 * Authenticated download URL for `<a href>` / window.open — no custom headers
 * possible, so `module=drive` is the only routing signal master gets. Prefer
 * `downloadDriveFile` for in-app downloads (header-based).
 */
export function resolveDriveDownloadUrl({ mountId, path }) {
  return resolveDriveUrl(`/mounts/${encodeURIComponent(mountId)}/download`, {
    path,
    module: driveConfig.module,
  });
}

export function normalizeDriveMounts(body) {
  const mounts = getCollection(body, ['mounts', 'data.mounts', 'data', 'items']);

  return mounts.map((mount) => normalizeDriveMount(mount)).filter(Boolean);
}

export function normalizeDriveEntries(body) {
  const entries = getCollection(body, ['entries', 'files', 'items', 'data.entries', 'data.files', 'data']);

  return entries.map((entry) => normalizeDriveEntry(entry)).filter(Boolean);
}

export function normalizeDriveShares(body) {
  const shares = getCollection(body, ['shares', 'data.shares', 'data', 'items']);

  return shares.map((share) => normalizeDriveShare(share)).filter(Boolean);
}

export function normalizeDriveUsage(body) {
  return {
    free: Number(body?.free) || 0,
    total: Number(body?.total) || 0,
    used: Number(body?.used) || 0,
  };
}

export function normalizeDriveIndexResponse(body) {
  const entries = Array.isArray(body?.entries) ? body.entries : [];
  return {
    mountId: normalizeDriveId(body?.mountId),
    query: String(body?.query || ''),
    subtree: String(body?.subtree || ''),
    limit: Number(body?.limit) || entries.length,
    entries,
  };
}

export class DriveApiError extends Error {
  constructor(body, status) {
    super(getDriveErrorMessage(body, status));
    this.name = 'DriveApiError';
    this.body = body;
    this.code = String(body?.error || body?.code || '');
    this.mountId = normalizeDriveId(body?.mountId ?? body?.mount_id);
    this.mountName = String(body?.mountName || body?.mount_name || '');
    this.status = status;
  }
}

export function isDriveCredentialsError(error) {
  const status = Number(error?.status || error?.response?.status);
  const code = String(
    error?.code ||
      error?.body?.error ||
      error?.body?.code ||
      error?.response?.data?.error ||
      error?.response?.data?.code ||
      '',
  );

  return status === 401 && (code === 'credentials_required' || code === 'credentials_invalid');
}

export function isDriveIndexUnavailableError(error) {
  return (
    error &&
    (error.status === 503 ||
      String(error?.code || error?.errorCode || '') === 'index_not_available' ||
      String(error?.message || '').toLowerCase().includes('index not available'))
  );
}

function normalizeDriveMount(mount) {
  const id = normalizeDriveId(mount?.id);
  if (!id) {
    return null;
  }

  const authMode = normalizeMountAuthMode(mount?.authMode ?? mount?.auth_mode);

  return {
    ...mount,
    authMode,
    credentialsVerifiedAt: normalizeCredentialTimestamp(mount?.credentialsVerifiedAt ?? mount?.credentials_verified_at),
    hasCredentials: normalizeMountHasCredentials(mount, authMode),
    id,
    name: String(mount.name || `Mount ${id}`),
    permission: normalizeNullableEnum(mount.permission || mount.access?.[0]?.permission),
    type: normalizeNullableEnum(mount.type) || 'drive',
  };
}

function normalizeDriveMountCredentialStatus(body) {
  const authMode = normalizeMountAuthMode(body?.authMode ?? body?.auth_mode);

  return {
    ...body,
    authMode,
    credentialsVerifiedAt: normalizeCredentialTimestamp(body?.credentialsVerifiedAt ?? body?.credentials_verified_at),
    hasCredentials: normalizeMountHasCredentials(body, authMode),
  };
}

function normalizeDriveEntry(entry) {
  const name = String(entry?.name || '').trim();
  const path = String(entry?.path || name).replace(/^\/+/, '');

  if (!name && !path) {
    return null;
  }

  return {
    ...entry,
    // The document id survives rename and move; the path does not. Anything
    // that wants to keep pointing at this object across a reorganisation
    // holds on to the id and treats the path as the label.
    //
    // Empty is a normal answer, not a failure: mounts whose provider has no
    // trustworthy change token deliberately get no identities at all.
    documentId: String(entry?.documentId || ''),
    // Monotonic per object. Unlike the etag it is ordered, so a client can
    // tell "newer" from merely "different" — which is what a live-refreshing
    // editor needs. 0 means unknown.
    revision: Number(entry?.revision) || 0,
    etag: String(entry?.etag || ''),
    isDir: Boolean(entry?.isDir ?? entry?.isDirectory ?? entry?.directory),
    mimeType: String(entry?.mimeType || entry?.mime || ''),
    // The backend's facts about the format; empty when it does not know it.
    formatKey: String(entry?.formatKey || ''),
    mediaType: String(entry?.mediaType || ''),
    // Only on `preview: true`: ready | pending | unavailable | failed, with text once ready.
    preview: entry?.preview
      ? { status: String(entry.preview.status || ''), text: String(entry.preview.text || '') }
      : null,
    modifiedAt: String(entry?.modifiedAt || entry?.updatedAt || ''),
    name: name || path.split('/').filter(Boolean).pop() || path,
    path,
    size: Number(entry?.size) || 0,
  };
}

function normalizeDriveShare(share) {
  const id = normalizeDriveId(share?.id);
  const mountId = normalizeDriveId(share?.mountId);
  const token = normalizeNullableString(share?.token);

  if (!id && !token) {
    return null;
  }

  return {
    ...share,
    expiresAt: normalizeNullableString(share?.expiresAt),
    id,
    mountId,
    documentId: normalizeNullableString(share?.documentId),
    path: normalizeNullableString(share?.path),
    permission: normalizeNullableEnum(share?.permission) || 'read',
    token,
    type: normalizeNullableEnum(share?.type) || 'link',
  };
}

async function driveRequest({ body, method = 'GET', path, query, headers = {} }) {
  const response = await fetchWithAuth(resolveDriveUrl(path, query), {
    body: body ? JSON.stringify(body) : undefined,
    headers: { ...createDriveHeaders(), ...headers },
    method,
  });
  const responseBody = await readDriveBody(response);

  if (!response.ok) {
    throw createDriveError(responseBody, response.status);
  }

  return responseBody;
}

function createDriveError(body, status) {
  return new DriveApiError(body, status);
}

// Non-auth headers only — `fetchWithAuth` (or `buildAuthHeaders` for the XHR
// upload path) injects the shell-owned `Authorization` bearer.
function createDriveHeaders({ accept = 'application/json', isMultipart = false } = {}) {
  const headers = {
    Accept: accept,
    'BLP-Module': driveConfig.module,
  };

  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

// Build an authenticated drive API URL. Routing to the drive process is done
// via the `BLP-Module` header from createDriveHeaders — do NOT blanket-append
// `?module=drive` here. The query fallback is only for paths that cannot send
// custom headers (`resolveDriveDownloadUrl`, anonymous `buildShareTokenUrl`).
function resolveDriveUrl(path, query = {}) {
  const baseUrl = getApiOrigin();
  const endpoint = driveConfig.endpoint.replace(/\/+$/, '');
  const url = new globalThis.URL(`${endpoint}/${String(path || '').replace(/^\/+/, '')}`, `${baseUrl}/`);

  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

// Anonymous share-token URLs (`PublicLayout`, no auth session). When the public
// page passes an explicit `baseUrl`, strip the `/v1` endpoint + take its origin;
// otherwise fall back to the shell-resolved origin (`getApiOrigin`, no `/v1`).
function resolveDriveApiBase(baseUrl) {
  const endpoint = driveConfig.endpoint.replace(/\/+$/, '');
  const configuredBase = String(baseUrl || '').trim();
  const apiOrigin = configuredBase ? originOf(stripDriveApiEndpoint(configuredBase)) : getApiOrigin();

  return `${apiOrigin.replace(/\/+$/, '')}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
}

function originOf(value) {
  const raw = String(value || '').trim();
  try {
    return new globalThis.URL(raw).origin;
  } catch {
    return raw.replace(/\/+$/, '');
  }
}

function stripDriveApiEndpoint(value) {
  const endpoint = driveConfig.endpoint.replace(/^\/?/, '/').replace(/\/+$/, '');
  const pattern = new RegExp(`${escapeRegExp(endpoint)}\\/?$`);
  return String(value || '').trim().replace(pattern, '').replace(/\/+$/, '');
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function collectDriveDownload(response, onProgress, path = '') {
  const fileName = getDriveDownloadFileName(response, path);
  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  const total = Number(response.headers.get('content-length')) || 0;

  if (response.body && typeof response.body.getReader === 'function') {
    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      chunks.push(value);
      loaded += value.byteLength;
      notifyDriveDownloadProgress(onProgress, loaded, total);
    }

    notifyDriveDownloadProgress(onProgress, loaded, total || loaded);
    return {
      blob: new globalThis.Blob(chunks, { type: contentType }),
      fileName,
    };
  }

  const blob = await response.blob();
  notifyDriveDownloadProgress(onProgress, blob.size, total || blob.size);

  return {
    blob,
    fileName,
  };
}

async function readDriveBody(response) {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json') && !contentType.includes('text/')) {
    return response.blob ? response.blob() : null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function getDriveErrorMessage(body, status) {
  return body?.message || body?.error || body?.code || `Drive API Fehler (${status})`;
}

function getDriveDownloadFileName(response, path) {
  const disposition = response.headers.get('content-disposition') || '';
  const encodedFilename = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)?.[1];

  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename.trim().replace(/^"|"$/g, ''));
    } catch {
      return encodedFilename.trim().replace(/^"|"$/g, '');
    }
  }

  const filename = /filename=([^;]+)/i.exec(disposition)?.[1]?.trim().replace(/^"|"$/g, '');
  if (filename) {
    return filename;
  }

  return String(path || 'download').split('/').filter(Boolean).pop() || 'download';
}

function notifyDriveDownloadProgress(onProgress, loaded, total) {
  if (typeof onProgress !== 'function') {
    return;
  }

  onProgress({
    loaded,
    percent: total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0,
    total,
  });
}

function getCollection(body, paths) {
  if (Array.isArray(body)) {
    return body;
  }

  for (const path of paths) {
    const value = getNestedValue(body, path);
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}

function getNestedValue(source, path) {
  return String(path)
    .split('.')
    .reduce((value, key) => (value && typeof value === 'object' ? value[key] : undefined), source);
}

function normalizeDriveId(value) {
  if (value && typeof value === 'object') {
    return normalizeDriveId(value.int64 ?? value.Int64 ?? value.value);
  }

  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function normalizeNullableEnum(value) {
  if (value && typeof value === 'object') {
    if (value.valid === false || value.Valid === false) {
      return '';
    }

    return String(value.string || value.String || value.value || value.Value || '').trim();
  }

  return String(value || '').trim();
}

function normalizeNullableString(value) {
  if (value && typeof value === 'object') {
    if (value.valid === false || value.Valid === false) {
      return '';
    }

    return String(value.string || value.String || value.value || value.Value || value.time || value.Time || '').trim();
  }

  return String(value || '').trim();
}

function normalizeMountAuthMode(value) {
  const authMode = normalizeNullableEnum(value).toLowerCase();
  return authMode === 'per_user' ? 'per_user' : 'shared';
}

function normalizeMountHasCredentials(source, authMode) {
  if (source?.hasCredentials !== undefined) {
    return Boolean(source.hasCredentials);
  }
  if (source?.has_credentials !== undefined) {
    return Boolean(source.has_credentials);
  }

  return authMode === 'shared';
}

function normalizeCredentialTimestamp(value) {
  const timestamp = String(value || '').trim();
  return timestamp || null;
}
